/**
 * Modal axis wired to edge imports (Phase 2, Deliverable B).
 *
 * Realizes the proved-engine v03 proposal item 2 ("Wire M to edge imports"): the
 * typed-edge discipline says only a `constraint` edge refutes (a verdict effect,
 * handled by `importEdge`); a `heuristic` edge is configuration-inert but RAISES
 * the explored claim's modal grade M; a `resource` edge enables a warrant-move
 * but by itself changes no claim's standing; a `shared-foundation` edge is inert.
 *
 * This module computes ONLY the profile (modal) effect. The configuration effect
 * stays in `importEdge`, so the Phase 1 verdict path is unchanged: for inert
 * edges `state` and `configDigest` are untouched.
 *
 * Modal grade ladder (§1.3; M is Nat, 0 strongest):
 *   M0 necessary · M1 physically possible · M2 conceptually possible ·
 *   M3 contested · M4 impossible/refuted.
 *
 * DIRECTION (logged D-P2-1): a `heuristic` "raises M" numerically — it moves the
 * explored branch toward *contested* (the worked example: Lakatos's "explore the
 * monster" raises M of the explored branch). W and E are never touched (raises M
 * only, never W/E).
 *
 * REAL SURFACE (Phase 3, replaces the Phase 2 increment stub; logged D-P3-1): the
 * lift is **monotone** (it never lowers M) and **idempotent** (applying the same
 * heuristic edge twice equals applying it once). Both hold because the lift is a
 * clamp `M ← max(M, contested)` rather than an increment: a heuristic flags the
 * explored branch as *at least contested* (M3). It never crosses a more-settled
 * grade into `impossible` (M4) — a heuristic never refutes; an already-impossible
 * claim (M4) is left untouched.
 */
import { deepFreeze } from './immutable.js';
/** Named modal grades (Nat, 0 strongest). */
export const Modal = {
    necessary: 0,
    physicallyPossible: 1,
    conceptuallyPossible: 2,
    contested: 3,
    impossible: 4,
};
/** The modal grade a `heuristic` edge lifts the explored branch toward (and to). */
export const HEURISTIC_MODAL_TARGET = Modal.contested; // 3
/** @deprecated Phase 2 name retained for compatibility; equals the target grade. */
export const HEURISTIC_MODAL_CEILING = HEURISTIC_MODAL_TARGET;
/**
 * The modal effect of importing an edge on the explored claim's profile.
 *   - `heuristic`: lifts M to at least `contested` — `M ← max(M, M3)`. Monotone
 *     (never lowers M) and idempotent (re-applying changes nothing). W and E
 *     unchanged. Never moves a sub-impossible claim into M4.
 *   - `resource`, `constraint`, `shared-foundation`: profile unchanged
 *     (a constraint's force is on the configuration/verdict, not the profile;
 *     a resource only *enables* a separate W-move; shared-foundation is inert).
 *
 * Pure and total; returns a frozen Profile.
 */
export function applyEdgeToProfile(kind, profile) {
    if (kind === 'heuristic') {
        // Clamp toward contested: monotone (never lowers) and idempotent (max is
        // idempotent). An already-more-contested claim (incl. M4) is left untouched.
        const m = Math.max(profile.m, HEURISTIC_MODAL_TARGET);
        return deepFreeze({ w: profile.w, e: profile.e, m });
    }
    // resource / constraint / shared-foundation leave the profile unchanged.
    return deepFreeze({ w: profile.w, e: profile.e, m: profile.m });
}
//# sourceMappingURL=modal.js.map